import { useState } from 'react'
import { useNavigate } from 'react-router-dom'
import { Plus, Truck } from '@phosphor-icons/react'
import { DataTable, EmptyState, type Column } from '@/ui'
import { errorMessage } from '../../api/core'
import { useOrgs } from '../../api/reference'
import { useDispatches, usePackLotChoices } from '../../api/recall'
import type { DispatchRow } from '../../model/recall/mappers'
import { ActionButton } from '../../components/Can'
import { DateFilter, FilterBar, SearchFilter, SelectFilter } from '../../components/FilterBar'
import { ConsolePage } from '../../components/Layout'
import { Code, Status, When } from '../../components/bits'
import { NewDispatchDialog } from '../../components/recall/NewDispatchDialog'
import { useUrlState } from '../../lib/useUrlState'
import { dispatchStatusLabel } from '../../model/recall/words'

const LIMIT = 25

/** Dispatches — F7.8: trips of released pouches to customers. */
export function DispatchesPage() {
  const navigate = useNavigate()
  const [creating, setCreating] = useState(false)
  const [f, setF, { reset, active }] = useUrlState({ toOrgId: '', packLotId: '', search: '', dateFrom: '', dateTo: '', page: 1 })
  const buyers = useOrgs('BUYER')
  const packLots = usePackLotChoices()
  const q = useDispatches({
    page: f.page,
    limit: LIMIT,
    toOrgId: f.toOrgId || undefined,
    packLotId: f.packLotId || undefined,
    search: f.search || undefined,
    dateFrom: f.dateFrom || undefined,
    dateTo: f.dateTo || undefined,
  })

  const columns: Column<DispatchRow>[] = [
    { id: 'code', header: 'Trip', cell: (r) => <Code>{r.code}</Code> },
    { id: 'customer', header: 'Customer', cell: (r) => r.toOrg ?? <span className="text-muted">—</span> },
    { id: 'challan', header: 'Invoice / challan', cell: (r) => <Code>{r.challanNo}</Code>, hideBelow: 'md' },
    {
      id: 'lots',
      header: 'Pack lots',
      hideBelow: 'sm',
      cell: (r) => (
        <span className="flex flex-col font-mono text-[12.5px] leading-tight">
          {r.items.slice(0, 2).map((i) => (
            <span key={i.itemId}>{i.packLotCode}</span>
          ))}
          {r.packLots > 2 && <span className="text-muted">+{r.packLots - 2} more</span>}
        </span>
      ),
    },
    { id: 'pouches', header: 'Pouches', align: 'right', mono: true, cell: (r) => r.pouches },
    { id: 'vehicle', header: 'Vehicle', cell: (r) => <Code>{r.vehicleNo}</Code>, hideBelow: 'lg' },
    { id: 'from', header: 'From', cell: (r) => r.fromLocation ?? '—', hideBelow: 'lg' },
    { id: 'at', header: 'Left', cell: (r) => <When at={r.dispatchedAt} />, hideBelow: 'md' },
    { id: 'status', header: 'Status', cell: (r) => <Status status={r.status} label={dispatchStatusLabel(r.status)} /> },
  ]

  return (
    <ConsolePage
      eyebrow="Pack & lab"
      title="Dispatches"
      description="Released pouches sent to customers: which pack lots, how many pouches, the invoice and the vehicle."
      actions={
        <ActionButton perm="pack-lots:dispatch" leadingIcon={<Plus />} onClick={() => setCreating(true)}>
          New dispatch
        </ActionButton>
      }
    >
      <DataTable<DispatchRow>
        caption="Dispatches"
        columns={columns}
        rows={q.data?.rows ?? []}
        getRowId={(r) => r.id}
        loading={q.isLoading}
        error={q.isError ? errorMessage(q.error) : undefined}
        onRowClick={(r) => navigate(`/c/dispatches/${r.id}`)}
        pageSize={LIMIT}
        server={{ page: f.page, total: q.data?.total ?? 0, onPageChange: (page) => setF({ page }) }}
        filters={
          <FilterBar onClear={reset} active={active}>
            <SearchFilter label="Trip or invoice" value={f.search} onChange={(search) => setF({ search })} placeholder="e.g. INV-114" />
            <SelectFilter
              label="Customer"
              value={f.toOrgId}
              onChange={(toOrgId) => setF({ toOrgId })}
              options={(buyers.data ?? []).filter((o) => o.kind === 'BUYER').map((o) => ({ value: o.id, label: o.name }))}
            />
            <SelectFilter
              label="Pack lot"
              value={f.packLotId}
              onChange={(packLotId) => setF({ packLotId })}
              options={(packLots.data ?? []).map((l) => ({ value: l.id, label: l.code }))}
            />
            <DateFilter label="Left from" value={f.dateFrom} onChange={(dateFrom) => setF({ dateFrom })} />
            <DateFilter label="Left until" value={f.dateTo} onChange={(dateTo) => setF({ dateTo })} />
          </FilterBar>
        }
        empty={
          <EmptyState icon={<Truck />} title="No dispatches match" description="A dispatch is recorded at the packing hall when released cartons leave for a customer." />
        }
      />
      <NewDispatchDialog open={creating} onClose={() => setCreating(false)} />
    </ConsolePage>
  )
}
